import React, {useState, useEffect, useContext, useRef} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, Stack, Typography, useTheme} from "@mui/material";
import {DeleteRounded, DownloadRounded, EditRounded, ExpandMore, FileUploadRounded} from "@mui/icons-material";
import {useFieldArray, useForm, FormProvider } from "react-hook-form";
import {useTranslation} from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError } from "axios";
import { useRecoilState } from "recoil";
import { FormAutocomplete, FormDatePicker, FormTextField } from "../../../common/form-fields-wrappers";
import { documentsDmsState, generalInfoState } from "../../../search-dms/state";
import { insertDocumentDms, getDocumentDms, deleteDocumentDms } from "../../../search-dms/dmsSearch.actions";
import { DMS_DOCUMENTS_FORM_VALIDATION_SCHEMA } from "../../../search-dms/config";
import DocumentalHistory from "../../../documental-history/DocumentalHistory";
import CustomLoader from "../../../../../util/component/CustomLoader";
import {setSnackbar} from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {NetworkScope} from "../../../types";
import {useHasScope} from "../../../../../config/globalConfig";

const DocumentRepositoryDms = () => {
    const {t} = useTranslation(['network', 'translation']);
    const theme = useTheme();
    const hasScope = useHasScope;
    const typeList = ["Insurance document", "Contract document", "DMS data file", "Branding doc", "Termination agreement", "Other"];
    const [documentsDms, setDocumentDms] = useRecoilState(documentsDmsState);
    const [isDisabled, setIsDisabled] = useState(true);
    const [generalInfo, setGeneralInfo] = useRecoilState(generalInfoState);
    const [isFetching, setIsFetching] = useState(false);
    const [update, setUpdate] = useState(true);
    const fetchedDocIdsRef = useRef<Set<string | number>>(new Set());
    const deletedDocIdsRef = useRef<Set<string | number>>(new Set());
    const resetFetchedDocIds = () => {
        fetchedDocIdsRef.current.clear();
    };
    const resetDeletedDocIds = () => {
        deletedDocIdsRef.current.clear();
    };
    // Use Context
    const { dispatchGlobalData } = useContext(GlobalContext);
    const handleDisableForm = (value:boolean) => {
        setIsDisabled(value);
    }
    const [openHistory, setOpenHistory] = useState(false);
    const MAX_UPLOAD_BYTES = 70 * 1024 * 1024;

    const documentsForm = useForm({
        defaultValues: {
            documents: documentsDms ?? []
        },
        resolver: yupResolver(
            DMS_DOCUMENTS_FORM_VALIDATION_SCHEMA
        )
    });

    const areValuesEqual = (a: any, b: any) => {
        if (a === b) return true;
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() === b.getTime();
        }
        return JSON.stringify(a) === JSON.stringify(b);
    };

    const markFieldAsUpserted = (index: number, field: string, newValue: any, options?: {force?: boolean}) => {
        //console.log('markFieldAsUpserted');
        const currentValue = documentsForm.getValues(`documents.${index}.${field}`);
        if (options?.force || !areValuesEqual(currentValue, newValue)) {
            documentsForm.setValue(`documents.${index}.upserted`, true, { shouldDirty: true });
        }
    };

    const onSubmit = async (data: any) => {
        setIsFetching(true);
        try {
            // delete removed documents first
            for (const deletedId of Array.from(deletedDocIdsRef.current)) {
                await deleteDocumentDms(deletedId);
            }
            resetDeletedDocIds();
            const docsToUpsert = (data.documents || []).filter((doc: any) => doc?.upserted === true);
            const docsNotUpserted = (data.documents || []).filter((doc: any) => doc?.upserted !== true);
            const collectedDocuments: any[] = [];
            for (const doc of docsToUpsert) {
                const { upserted, ...payload } = doc || {};
                const reqParameters = {
                    id: generalInfo.id,
                    documents: [payload]
                };
                const resp = await insertDocumentDms(reqParameters);
                //console.log(resp);
                if (Array.isArray(resp?.documents)) {
                    collectedDocuments.push(...resp.documents.map((d: any) => ({ ...d, upserted: false })));
                }
            }
            const finalList = [...docsNotUpserted.map((doc: any) => ({ ...doc, upserted: false })), ...collectedDocuments];
            if (finalList.length > 0) {
                resetFetchedDocIds();
                setDocumentDms(finalList);
            }
            setIsDisabled(true);
        } catch (err) {
            dispatchGlobalData({type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('Error'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        } finally {
            setIsFetching(false);
        }
    }

    const onSubmitError = (errors: any) => {
        console.log('onSubmit errors', errors);
    }

    const documentsArray = useFieldArray({name: 'documents', control: documentsForm.control});

    const handleAddDocument = () => {
        documentsArray.append({
            title: "Other",
            file: "",
            fileName: "",
            note: "",
            validityStartDate: null,
            validityEndDate: null,
            validityAnnualPremiumEndDate: null,
            upserted: true,
        });
        //console.log( 'documents' , documentsArray);
    }

    const handleOnDelete = async (index?: number) => {
        if (index === undefined) return;
        const docToRemove = documentsForm.getValues(`documents.${index}`);
        if (docToRemove?.id) {
            deletedDocIdsRef.current.add(docToRemove.id);
        }
        //console.log('deleting document', docToRemove);
        documentsArray.remove(index);

    }

    const handleFileOnChange = (e: React.BaseSyntheticEvent, field: any, index: number) => {
        e.stopPropagation();
        if (!!e.target.files[0]?.size) {
            let reader = new FileReader();
            reader.readAsDataURL(e.target.files[0]);
            reader.onload = function () {
                if (e.target.files[0].size < MAX_UPLOAD_BYTES) {
                    documentsForm.setValue(field, reader.result, { shouldValidate: true, shouldDirty: true });
                    documentsForm.setValue(`documents.${index}.fileName`, e.target.files[0].name, { shouldValidate: true, shouldDirty: true });
                    documentsForm.setValue(`documents.${index}.upserted`, true, { shouldDirty: true });
                    documentsForm.trigger(field);
                    setUpdate(!update)
                } else {
                    dispatchGlobalData({type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('common.error.docSize'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }

            };
        }
    }

    const downloadFunc = (data: string, fileName?: string) => {
        const linkSource = data;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = fileName ? fileName : 'file';
        downloadLink.click();
    };

    const downloadDocument = (base64: string | ArrayBuffer, fileName?: string) => {
        //console.log('downloadDocument', base64);
        downloadFunc(base64.toString(), fileName);
    };

    const handleOpenHistory = () => setOpenHistory(true);
    const handleCloseHistory = () => setOpenHistory(false);

    const updatecomponent = () => {
        setUpdate(!update)
    }

    const dropChanges = () => {
        setDocumentDms(documentsDms.map((d: any) => ({ ...d, upserted: false })));
        documentsArray.replace(documentsDms);
        resetDeletedDocIds();
        resetFetchedDocIds();
        setIsDisabled(true);
        setUpdate(!update);
    }

    useEffect(() => {
    },[update])

    useEffect(() => {
        documentsForm.setValue("documents",documentsDms);
    },[documentsDms])


    useEffect(() => {
        if (!documentsDms || !Array.isArray(documentsDms) || documentsDms.length === 0) {
            return;
        }

        const docsToFetch = documentsDms
            .map((doc: any, idx: number) => ({ doc, idx }))
            .filter(({ doc }) => {
                const docId = doc?.id;
                return docId && !doc.file && !fetchedDocIdsRef.current.has(docId);
            });

        if (docsToFetch.length === 0) {
            return;
        }

        //console.log('get file');
        setIsFetching(true);

        //let isCancelled = false;
        //let inFlight = new Set<string | number>();

        const fetchDocumentBase64 = async (docId: string | number, idx: number) => {
            if (!docId) return;
            if (fetchedDocIdsRef.current.has(docId)) return;
            //if (inFlight.has(docId)) return;
            //inFlight.add(docId);

            fetchedDocIdsRef.current.add(docId);
            try {
                const resp = await getDocumentDms(docId);
                const fileBase64 = resp?.file;
                if (fileBase64) {
                    documentsForm.setValue(`documents.${idx}.file`, fileBase64, { shouldValidate: true });
                    documentsForm.trigger(`documents.${idx}.file`);
                    setUpdate(u => !u);
                }
            } catch (e) {
                fetchedDocIdsRef.current.delete(docId);
                console.warn('Error fetching document', docId, e);
                //if (!isCancelled) {
                    dispatchGlobalData({type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('network.documentRepositoryDms.error.fetchDocument') || t('Error fetching document'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                //}
            } finally {
                //inFlight.delete(docId);
            }
        }

        const pending = docsToFetch.map(({ doc, idx }) => fetchDocumentBase64(doc.id, idx));

        Promise.allSettled(pending).finally(() => {
            //if (!isCancelled) {
                setIsFetching(false);
            //}
        });

        return () => {
            //isCancelled = true;
            //inFlight.clear();
            //setIsFetching(false);
        }
    }, [documentsDms, update]);

    return (
        <>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary expandIcon={<ExpandMore/>}>
                            <Typography variant={"h6"}>
                                {t("dmsDetail.documentRepositoryDms.accordion.title")}
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <FormProvider {...documentsForm}>
                                <Grid item xs={12}>
                                    <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                        {hasScope(NetworkScope.MsMng) && isDisabled &&
                                            <IconButton color={"accent"} onClick={() => {handleDisableForm(false)}}>
                                                <EditRounded/>
                                            </IconButton>
                                        }
                                    </Stack>
                                </Grid>
                                {documentsArray.fields.length === 0 &&
                                    <Grid item container xs={12} md={12} align-items="center" justifyContent="flex">
                                        <p>{t("dmsDetail.documentRepositoryDms.message.emptyState")}</p>
                                    </Grid>
                                }
                                {documentsArray.fields.map((field, index) => {
                                    return (
                                        <Grid key={field.id} item container md={12} xs={12} columnSpacing={2}>
                                            {isDisabled === false ?
                                                <>
                                                    <Grid item md={2} xs={2}>
                                                        <FormAutocomplete
                                                            disabled={isDisabled}
                                                            name={`documents.${index}.title`}
                                                            control={documentsForm.control}
                                                            options={typeList}
                                                            label={t("dmsDetail.documentRepositoryDms.form.label.title")}
                                                            onInputChange={() => updatecomponent()}
                                                            onChange={(_, value) => markFieldAsUpserted(index, 'title', value)}
                                                        />
                                                    </Grid>
                                                    <Grid item md={2} xs={2}>
                                                        <FormTextField
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.note`}
                                                            label={t("dmsDetail.documentRepositoryDms.form.label.note")}
                                                            onChange={(e) => markFieldAsUpserted(index, 'note', e.target.value)}
                                                        />
                                                    </Grid>
                                                    <Grid container item md={7} xs={7} columnSpacing={2} >
                                                        {(documentsForm.getValues(`documents.${index}.title`) === "Insurance document" || documentsForm.getValues(`documents.${index}.title`) === "Contract document") &&
                                                            <Grid item md={4} xs={4}>
                                                                <FormDatePicker
                                                                    disabled={isDisabled}
                                                                    control={documentsForm.control}
                                                                    name={`documents.${index}.validityStartDate`}
                                                                    label={t("dmsDetail.documentRepositoryDms.form.label.validityStartDate")}
                                                                    onChange={(value) => markFieldAsUpserted(index, 'validityStartDate', value, {force: true})}
                                                                />
                                                            </Grid>}
                                                        {(documentsForm.getValues(`documents.${index}.title`) === "Insurance document" || documentsForm.getValues(`documents.${index}.title`) === "Contract document") &&
                                                            <Grid item md={4} xs={4}>
                                                                <FormDatePicker
                                                                    disabled={isDisabled}
                                                                    control={documentsForm.control}
                                                                    name={`documents.${index}.validityEndDate`}
                                                                    label={t("dmsDetail.documentRepositoryDms.form.label.validityStartDate")}
                                                                    onChange={(value) => markFieldAsUpserted(index, 'validityEndDate', value, {force: true})}
                                                                />
                                                            </Grid>}
                                                        {documentsForm.getValues(`documents.${index}.title`) === "Insurance document" &&
                                                            <Grid item md={4} xs={4}>
                                                                <FormDatePicker
                                                                    disabled={isDisabled}
                                                                    control={documentsForm.control}
                                                                    name={`documents.${index}.validityAnnualPremiumEndDate`}
                                                                    label={t("dmsDetail.documentRepositoryDms.form.label.validityAnnualPremiumEndDate")}
                                                                    onChange={(value) => markFieldAsUpserted(index, 'validityAnnualPremiumEndDate', value, {force: true})}
                                                                />
                                                            </Grid>}
                                                    </Grid>
                                                    <Grid item xs={1} md={1} sx={{display: "flex", justifyContent: "center"}}>
                                                        {documentsForm.getValues(`documents.${index}.file`) !== "" &&
                                                            <IconButton color="secondary" component="label"
                                                                        sx={{alignSelf: "end"}}>
                                                                <input hidden
                                                                       type="file"
                                                                       onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, `documents.${index}.file`, index)}
                                                                />
                                                                <FileUploadRounded/>
                                                            </IconButton>}

                                                        { documentsForm.getValues(`documents.${index}.file`) === "" &&
                                                            <IconButton color="error" component="label" sx={{alignSelf: "end"}}>
                                                                <input hidden accept="image/*"
                                                                       type="file"
                                                                       onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, `documents.${index}.file`, index)}
                                                                />
                                                                <FileUploadRounded/>
                                                            </IconButton>}

                                                        { documentsForm.getValues(`documents.${index}.file`) !== "" ?
                                                            <Box sx={{
                                                                display: "flex",
                                                                justifyContent: "flex-end",
                                                                alignSelf: "end"
                                                            }}>
                                                                <IconButton color="secondary"
                                                                            onClick={(e: React.SyntheticEvent) => downloadDocument(documentsForm.getValues(`documents.${index}.file`), documentsForm.getValues(`documents.${index}.title`))}>
                                                                    <DownloadRounded/>
                                                                </IconButton>
                                                            </Box>
                                                            : null}

                                                        <Box sx={{
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            alignSelf: "end"
                                                        }}>
                                                            <IconButton color="secondary" onClick={() => handleOnDelete(index)}>
                                                                <DeleteRounded/>
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                    {documentsForm.getValues(`documents.${index}.file`) === "" ?
                                                        <Grid item container xs={12} md={12}>
                                                            <Typography sx={{color: theme.palette.error.main}}>
                                                                {t("dmsDetail.documentRepositoryDms.form.label.uploadDocument")}
                                                            </Typography>
                                                        </Grid>
                                                        :
                                                        <Grid item container xs={12} md={12}>
                                                            <Typography sx={{color: theme.palette.secondary.main}}>
                                                                {documentsForm.getValues(`documents.${index}.fileName`)}
                                                            </Typography>
                                                        </Grid>}
                                                </>

                                                : <Grid container item md={12} xs={12} columnSpacing={2}>
                                                    <Grid item md={2} xs={2}>
                                                        <FormAutocomplete
                                                            disabled={isDisabled}
                                                            name={`documents.${index}.title`}
                                                            control={documentsForm.control}
                                                            options={typeList}
                                                            label={t("dmsDetail.documentRepositoryDms.form.label.title")}
                                                        />
                                                    </Grid>
                                                    <Grid item md={2} xs={2}>
                                                        <FormTextField
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.note`}
                                                            label={t("dmsDetail.documentRepositoryDms.form.label.note")}
                                                        />
                                                    </Grid>
                                                    <Grid container item md={8} xs={8} columnSpacing={2}>
                                                        {(documentsForm.getValues(`documents.${index}.title`) === "Insurance document" || documentsForm.getValues(`documents.${index}.title`) === "Contract document") &&
                                                            <Grid item md={4} xs={4}>
                                                                <FormDatePicker
                                                                    disabled={isDisabled}
                                                                    control={documentsForm.control}
                                                                    name={`documents.${index}.validityStartDate`}
                                                                    label={t("dmsDetail.documentRepositoryDms.form.label.validityStartDate")}
                                                                />
                                                            </Grid>}
                                                        {(documentsForm.getValues(`documents.${index}.title`) === "Insurance document" || documentsForm.getValues(`documents.${index}.title`) === "Contract document") &&
                                                            <Grid item md={4} xs={4}>
                                                                <FormDatePicker
                                                                    disabled={isDisabled}
                                                                    control={documentsForm.control}
                                                                    name={`documents.${index}.validityEndDate`}
                                                                    label={t("dmsDetail.documentRepositoryDms.form.label.validityEndDate")}
                                                                />
                                                            </Grid>}
                                                        {documentsForm.getValues(`documents.${index}.title`) === "Insurance document" &&
                                                            <Grid item md={4} xs={4}>
                                                                <FormDatePicker
                                                                    disabled={isDisabled}
                                                                    control={documentsForm.control}
                                                                    name={`documents.${index}.validityAnnualPremiumEndDate`}
                                                                    label={t("dmsDetail.documentRepositoryDms.form.label.validityAnnualPremiumEndDate")}
                                                                />
                                                            </Grid>
                                                        }

                                                    </Grid>
                                                    {documentsForm.getValues(`documents.${index}.file`) === "" ?
                                                        <Grid item container xs={12} md={12}>
                                                            <Typography sx={{color: theme.palette.error.main}}>
                                                                {t("dmsDetail.documentRepositoryDms.form.label.uploadDocument")}
                                                            </Typography>
                                                        </Grid>
                                                        :
                                                        <Grid item container xs={12} md={12}>
                                                            <Typography sx={{color: theme.palette.secondary.main}}>{documentsForm.getValues(`documents.${index}.fileName`)}</Typography>
                                                        </Grid>}
                                                </Grid>
                                            }
                                        </Grid>
                                    );
                                })}
                                {
                                    isDisabled === false &&
                                 <Grid container sx={{display: 'flex', justifyContent: "flex-end", alignItems: "flex-end"}}  >
                                    <Grid xs={12} md={6} item sx={{display: 'flex', justifyContent: {xs: 'center', md: 'flex-start'}, gap: {xs: 1, md: 2}, flexDirection: {xs: 'column', md: 'row'}, padding: {xs: 1, md: 0}}}>
                                        <Button onClick={handleOpenHistory} >
                                            {t("dmsDetail.documentRepositoryDms.button.archive")}
                                        </Button>
                                        <Button color={"accent"} onClick={handleAddDocument}>
                                            {t("dmsDetail.documentRepositoryDms.button.addDocument")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={12} md={6} item sx={{display: 'flex', justifyContent: {xs: 'center', md: 'flex-end'}, gap: {xs: 1, md: 2}, flexDirection: {xs: 'column', md: 'row'}, padding: {xs: 1, md: 0}}}>
                                        <Button color={"accent"} onClick={dropChanges}>
                                            {t("dmsDetail.documentRepositoryDms.button.cancel")}
                                        </Button>
                                        <Button type={"button"} onClick={() => documentsForm.handleSubmit(onSubmit, onSubmitError)()}>
                                            {t("dmsDetail.documentRepositoryDms.button.confirm")}
                                        </Button>
                                    </Grid>
                                </Grid>
                                }
                            </FormProvider>
                        </AccordionDetails>
                    </Accordion>

            <Dialog
                open={openHistory}
                onClose={handleCloseHistory}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    {t("dmsDetail.documentRepositoryDms.dialog.title")}
                </DialogTitle>
                <DialogContent>
                    <DocumentalHistory functionality={"DMS"} id={generalInfo.id} documentsTitles={typeList}/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHistory}>
                        {t("dmsDetail.documentRepositoryDms.dialog.action.cancel")}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomLoader loading={isFetching}/>
        </>
    )
}

export default DocumentRepositoryDms;
